import { useState } from "react";
import { Phone, Mail, MapPin, Clock, Send, Loader2, CheckCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useGooglePlaceHours } from "@/hooks/useGooglePlaceHours";
import { Skeleton } from "@/components/ui/skeleton";
import { supabase } from "@/integrations/supabase/client";
import LocationMap from "./LocationMap";
import { countryCodes } from "@/data/countryCodes";

const validateEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const whyContactUs = [
  "Free on-farm visit & flock assessment",
  
  "Competitive bulk pricing for 5+ tonne orders",
  "Technical guidance from experienced nutritionists",
  "Doorstep delivery within 24–48 hours",
];

const AppointmentSection = () => {
  const { toast } = useToast();
  const { hours, isOpen, loading: hoursLoading } = useGooglePlaceHours("Sandhu Feeds Assandh Karnal Haryana");
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "", email: "", phone: "", type: "", message: ""
  });
  const [countryCode, setCountryCode] = useState("+91-India");
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.name.trim().length < 2) { toast({ title: "Please enter your name", variant: "destructive" }); return; }
    if (!validateEmail(formData.email)) { toast({ title: "Please enter a valid email", variant: "destructive" }); return; }
    if (!/^[0-9]{10}$/.test(formData.phone.trim())) { toast({ title: "Please enter a valid 10-digit phone number", variant: "destructive" }); return; }
    if (!formData.type) { toast({ title: "Please select an inquiry type", variant: "destructive" }); return; }
    if (formData.message.trim().length < 10) { toast({ title: "Please describe your requirements (at least 10 characters)", variant: "destructive" }); return; }

    setSubmitting(true);
    try {
      const { data, error } = await supabase.functions.invoke("send-contact-email", {
        body: {
          name: formData.name.trim(),
          email: formData.email.trim(),
          phone: `${countryCode.split("-")[0]}${formData.phone.trim()}`,
          type: formData.type || "General",
          message: formData.message.trim(),
        },
      });

      if (error) throw error;

      toast({ title: "Message sent!", description: "We'll get back to you shortly." });
      setFormData({ name: "", email: "", phone: "", type: "", message: "" });
    } catch (err) {
      console.error("Contact form error:", err);
      toast({ title: "Failed to send message", description: "Please try again or call us directly.", variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <section id="appointment" className="py-24 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="section-heading">
          <h2>Get In Touch</h2>
          <p>Schedule a consultation with our nutrition experts or inquire about bulk orders.</p>
          <div className="accent-line" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
          {/* Left — Info */}
          <div className="lg:col-span-2 space-y-8">
            {/* Why Contact Us */}
            <div>
              <h3 className="text-lg font-bold text-foreground font-display mb-4">Why Contact Us?</h3>
              <ul className="space-y-3">
                {whyContactUs.map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle className="h-4 w-4 text-accent mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-muted-foreground font-body">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="text-2xl font-bold text-foreground font-display mb-6">Head Office</h3>
              <div className="space-y-5">
                {[
                  { icon: MapPin, title: "Address", lines: ["M/S Sandhu Feeds", "Jind Road, Assandh", "District Karnal, Haryana 132039"] },
                  { icon: Phone, title: "Phone", lines: ["+91 74199 00071", "+91 74199 00072", "+91 74199 00073", "+91 74199 00074"] },
                  { icon: Mail, title: "Email", lines: ["info@sandhufeeds.com"] },
                  { icon: Clock, title: "Hours", lines: hoursLoading ? [] : hours, loading: hoursLoading, isOpen },
                ].map((item: any, i) => (
                  <div key={i} className="flex gap-4">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <item.icon className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-foreground text-sm font-body">{item.title}</p>
                      </div>
                      {item.loading ? (
                        <div className="space-y-1.5 mt-1">
                          <Skeleton className="h-3 w-36" />
                          <Skeleton className="h-3 w-28" />
                        </div>
                      ) : (
                        item.lines.map((line: string, j: number) =>
                          line.includes("@") && !line.includes(" ") ? (
                            <a key={j} href={`mailto:${line}`} className="block text-sm text-muted-foreground font-body hover:text-primary transition-colors">{line}</a>
                          ) : (
                            <p key={j} className="text-sm text-muted-foreground font-body">{line}</p>
                          )
                        )
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right — Form */}
          <div className="lg:col-span-3">
            <div className="card-premium p-8">
              <h3 className="text-xl font-bold text-foreground font-display mb-6">Send Us a Message</h3>
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Input
                    placeholder="Full Name *"
                    value={formData.name}
                    onChange={(e) => handleChange("name", e.target.value)}
                    required
                    maxLength={50}
                    className="font-body"
                    disabled={submitting}
                  />
                  <Input
                    type="email"
                    placeholder="Email Address *"
                    value={formData.email}
                    onChange={(e) => handleChange("email", e.target.value)}
                    required
                    className="font-body"
                    disabled={submitting}
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex">
                    <Select value={countryCode} onValueChange={setCountryCode} disabled={submitting}>
                      <SelectTrigger className="w-[140px] rounded-r-none font-body text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {countryCodes.map((c) => (
                          <SelectItem key={`${c.code}-${c.country}`} value={`${c.code}-${c.country}`} className="text-xs">
                            {c.country} ({c.code})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      type="tel"
                      placeholder="XXXXXXXXXX"
                      value={formData.phone}
                      onChange={(e) => handleChange("phone", e.target.value.replace(/[^0-9]/g, '').slice(0, 10))}
                      required
                      maxLength={10}
                      className="font-body rounded-l-none"
                      disabled={submitting}
                    />
                  </div>
                  <Select onValueChange={(v) => handleChange("type", v)} disabled={submitting}>
                    <SelectTrigger className="font-body">
                      <SelectValue placeholder="Inquiry Type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="general">General Query</SelectItem>
                      <SelectItem value="product">Product Information</SelectItem>
                      <SelectItem value="bulk">Bulk Orders</SelectItem>
                      <SelectItem value="custom">General Inquiry</SelectItem>
                      <SelectItem value="consultation">Farm Consultation</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Textarea
                  placeholder="Tell us about your requirements (breed, flock size, current challenges) *"
                  value={formData.message}
                  onChange={(e) => handleChange("message", e.target.value)}
                  className="min-h-[120px] font-body"
                  maxLength={500}
                  required
                  disabled={submitting}
                />
                <button type="submit" className="btn-copper w-full inline-flex items-center justify-center" disabled={submitting}>
                  {submitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="mr-2 h-4 w-4" />
                      Send Message
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>

        {/* Map */}
        <div className="mt-16">
          <LocationMap />
        </div>
      </div>
    </section>
  );
};

export default AppointmentSection;
